/**
 * Milestone 5: Fast response body serialization.
 * Avoid synchronous pretty-print on receipt - use compact stringify for objects.
 * Pretty-print can be done asynchronously in the UI layer.
 *
 * Postman-style: We NEVER truncate the response body. Full payload (including large Base64
 * strings, 1000+ records, multi-MB JSON) is always preserved and passed through. No artificial
 * size or row limits at the UI/serialization layer.
 */
export function fastSerializeResponseBody(data: unknown): string {
  if (typeof data === 'string') return data;
  if (data === null || data === undefined) return '';
  try {
    return JSON.stringify(data);
  } catch {
    return String(data);
  }
}

const HUGE_RESPONSE_THRESHOLD = 100 * 1024; // 100KB
/** Decode/pretty-print/tokenize of bodies this large freezes Chromium (e.g. GitHub OpenAPI ~6.6MB). */
export const MASSIVE_RESPONSE_THRESHOLD = 1024 * 1024; // 1MB

/** Line count above which the response viewer uses virtual scrolling for smooth performance. */
export const VIRTUAL_SCROLL_LINE_THRESHOLD = 200;
/** Min length (chars) to use virtual scroll when there are few newlines (e.g. compact JSON before pretty-print). */
export const VIRTUAL_SCROLL_MIN_LENGTH = 50 * 1024; // 50KB

export function isHugeResponse(body: string): boolean {
  return body.length > HUGE_RESPONSE_THRESHOLD;
}

export function isMassiveResponse(body: string): boolean {
  return body.length > MASSIVE_RESPONSE_THRESHOLD;
}

/** True if the body looks like JSON without parsing the full payload. */
export function looksLikeJson(body: string): boolean {
  const s = String(body ?? '');
  for (let i = 0; i < s.length && i < 64; i++) {
    const c = s[i];
    if (c === ' ' || c === '\n' || c === '\r' || c === '\t') continue;
    return c === '{' || c === '[';
  }
  return false;
}

/** UTF-8 byte length. Skip TextEncoder on large bodies (JSON is almost entirely ASCII). */
export function decodedResponseByteLength(body: string): number {
  if (!body) return 0;
  if (body.length > 256 * 1024) return body.length;
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(body).length;
  }
  return body.length;
}

function headerValue(headers: Record<string, string> | undefined, name: string): string {
  if (!headers) return '';
  const target = name.toLowerCase();
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === target) return String(v ?? '');
  }
  return '';
}

/**
 * Size shown in the response meta bar. Prefer the wire/gzip size (Postman) when the
 * proxy reports X-Proxy-Transfer-Size or Content-Length is smaller than the decoded body.
 */
export function responseDisplaySizeBytes(
  body: string,
  headers?: Record<string, string>
): number {
  const transfer = Number(headerValue(headers, 'x-proxy-transfer-size'));
  if (Number.isFinite(transfer) && transfer > 0) return transfer;
  const decoded = decodedResponseByteLength(body || '');
  const contentLength = Number(headerValue(headers, 'content-length'));
  if (Number.isFinite(contentLength) && contentLength > 0) {
    if (decoded > 0) return Math.min(contentLength, decoded);
    return contentLength;
  }
  return decoded;
}

export function isLargeEnoughForVirtualScroll(text: string): boolean {
  if (!text) return false;
  if (text.length >= VIRTUAL_SCROLL_MIN_LENGTH) return true;
  let count = 0;
  for (let i = 0; i < text.length && count <= VIRTUAL_SCROLL_LINE_THRESHOLD; i++) {
    if (text[i] === '\n') count++;
  }
  return count > VIRTUAL_SCROLL_LINE_THRESHOLD;
}

/**
 * Pretty-print JSON synchronously. Use for non-huge bodies so Pretty tab shows immediately.
 * For huge responses returns the full raw string (no truncation) to avoid blocking the UI;
 * LazyResponseBody still displays the complete content and may pretty-print in the background.
 */
export function prettyPrintJsonSync(raw: string): string {
  if (!raw || raw.trim() === '') return '';
  if (isHugeResponse(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return raw;
  }
}

/**
 * Pretty-print JSON asynchronously. For non-huge bodies uses sync path so callback runs immediately (no delay).
 * Kept for API compatibility; callers get result without requestIdleCallback delay.
 */
export function prettyPrintJsonAsync(
  raw: string,
  onPretty: (pretty: string) => void
): void {
  if (isHugeResponse(raw)) {
    onPretty(raw);
    return;
  }
  // Sync for typical response sizes so Pretty loads immediately with no "loading" delay
  try {
    const parsed = JSON.parse(raw);
    onPretty(JSON.stringify(parsed, null, 2));
  } catch {
    onPretty(raw);
  }
}

/**
 * Format HTML for readable display (line breaks after tags + indentation, Postman-style).
 */
export function formatHtmlForDisplay(html: string): string {
  if (!html || html.trim() === '') return '';
  if (isMassiveResponse(html)) return html;
  const withNewlines = html
    .replace(/>\s*</g, '>\n<')
    .replace(/\s*\n\s*/g, '\n')
    .trim();
  const lines = withNewlines.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return '';
  const indentStr = '  ';
  let indent = 0;
  const result: string[] = [];
  for (const line of lines) {
    const isClosing = /^\s*<\//.test(line);
    const isSelfClosing = /\/\s*>$/.test(line) || /^<\s*[\w-]+\s*\/\s*>/.test(line);
    const isDoctypeOrComment = /^\s*<(!DOCTYPE|!--|\?)/i.test(line);
    if (isClosing) {
      indent = Math.max(0, indent - 1);
      result.push(indentStr.repeat(indent) + line);
    } else if (isDoctypeOrComment || isSelfClosing) {
      result.push(indentStr.repeat(indent) + line);
    } else {
      result.push(indentStr.repeat(indent) + line);
      if (!isSelfClosing && /<\s*[\w-]+/.test(line) && !line.startsWith('</')) indent += 1;
    }
  }
  return result.join('\n');
}

/**
 * Pretty-print XML with line breaks and indentation. Returns raw string if parsing fails.
 */
export function formatXml(xml: string): string {
  if (!xml || xml.trim() === '') return '';
  if (isMassiveResponse(xml)) return xml;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'text/xml');
    const parseError = doc.querySelector('parsererror');
    if (parseError) return xml;
    const serialized = new XMLSerializer().serializeToString(doc);
    const withNewlines = serialized.replace(/>\s*</g, '>\n<');
    const lines = withNewlines.split('\n').map((l) => l.trim()).filter(Boolean);
    const indentStr = '  ';
    let indent = 0;
    const result: string[] = [];
    for (const line of lines) {
      if (line.startsWith('</')) {
        indent = Math.max(0, indent - 1);
        result.push(indentStr.repeat(indent) + line);
      } else if (line.startsWith('<?') || line.startsWith('<!') || /\/\s*>$/.test(line)) {
        result.push(indentStr.repeat(indent) + line);
      } else {
        result.push(indentStr.repeat(indent) + line);
        if (!line.startsWith('</')) indent += 1;
      }
    }
    return result.join('\n');
  } catch {
    return xml;
  }
}
